/**
 * Meridian artifacts — part type + schema.
 *
 * An ARTIFACT is a substantial, standalone deliverable (document, code file,
 * HTML snippet, data table) the model produces via the `create_artifact` tool.
 * The full content travels to the client as a `data-artifact` UI message part
 * (AI SDK v6 custom data part, emitted through the stream writer) and renders
 * in a dedicated card/viewer — it never echoes back into model context (only a
 * small ack does), which is the token win.
 *
 * Persistence: `data-artifact` parts live inside `meridian_messages.parts`
 * jsonb like every other part — NO dedicated table/migration for v1.
 * Future seam: if artifacts ever need versioning, sharing, or sizes beyond
 * MERIDIAN_ARTIFACT_MAX_CHARS, graduate to a standalone artifacts table with
 * S3 body offload (register a use case per WTC_S3_USE_CASE_CATALOG) and keep
 * only a pointer in the part. The `version` field below is the hook for that
 * (and for future streaming updates that rewrite the same part id).
 */

import { z } from 'zod';

/** UI message part type for artifacts (AI SDK data part naming: `data-<name>`). */
export const ARTIFACT_PART_TYPE = 'data-artifact' as const;

/** Data-part NAME (the part type without the `data-` prefix) — the key used in `dataSchemas`. */
export const ARTIFACT_DATA_NAME = 'artifact' as const;

/**
 * Supported artifact kinds.
 *
 * `chart` content is a JSON chart spec validated against `ChartSpecSchema` from
 * `@wtc/ui` — the SAME engine the website blog renders ```chart blocks with, so
 * there is one chart implementation, not two. Validation happens at CREATE time
 * (see `create-artifact-tool`) so a malformed spec comes back to the model as a
 * recoverable error instead of reaching the user as a broken card.
 */
export const ARTIFACT_KINDS = ['markdown', 'code', 'html', 'table', 'chart', 'image'] as const;

export type ArtifactKind = (typeof ARTIFACT_KINDS)[number];

/**
 * Kinds the MODEL may create through `create_artifact`.
 *
 * `image` is excluded deliberately. An image artifact's `content` is a URL, and
 * a model-authored `<img src>` would be an arbitrary outbound request from the
 * user's browser — a tracking pixel or an IP leak dressed up as a deliverable.
 * Image artifacts are emitted ONLY by the image tools, which point at bytes they
 * just stored themselves; the renderer independently refuses any src that is not
 * a same-origin Hub image path (see {@link isRenderableArtifactImageSrc}).
 */
export const MODEL_CREATABLE_ARTIFACT_KINDS = [
  'markdown',
  'code',
  'html',
  'table',
  'chart',
] as const satisfies readonly ArtifactKind[];

export type ModelCreatableArtifactKind = (typeof MODEL_CREATABLE_ARTIFACT_KINDS)[number];

/**
 * The one URL shape an `image` artifact may point at: Hub's authenticated,
 * same-origin image route. Enforced at RENDER time as well as at emit time,
 * because a persisted part could have been hand-edited in the database.
 */
export const ARTIFACT_IMAGE_PATH_PREFIX = '/api/meridian/images/';

/** Is this image artifact `content` safe to put in an `<img src>`? */
export function isRenderableArtifactImageSrc(src: string): boolean {
  // Same-origin absolute path only. Rejects http(s):, data:, blob:,
  // protocol-relative `//evil.example`, and any `..` traversal.
  return (
    src.startsWith(ARTIFACT_IMAGE_PATH_PREFIX) &&
    !src.startsWith('//') &&
    !src.includes('..')
  );
}

/**
 * Zod schema for the `data` payload of a `data-artifact` part.
 * Registered with `validateUIMessages({ dataSchemas })` so persisted artifact
 * parts survive reload validation (and junk is rejected).
 */
export const artifactDataSchema = z.object({
  /** Stable artifact id (also mirrored as the part-level `id` for stream reconciliation). */
  id: z.string().min(1),
  /** Short human title shown on the card and viewer. */
  title: z.string().min(1),
  /** What the content is — drives the renderer. */
  kind: z.enum(ARTIFACT_KINDS),
  /** Language hint for `kind: 'code'` (e.g. "typescript", "python"). */
  language: z.string().optional(),
  /** The full artifact content (markdown text, source code, HTML, or JSON rows). */
  content: z.string(),
  /** Monotonic version — 1 for v1; the seam for future streaming/edit updates. */
  version: z.number().int().min(1),
  /**
   * For `kind: 'image'` only: facts about the file `content` points at.
   *
   * Needed because an image artifact's `content` is a URL, so the usual
   * `content.length` size label would describe the URL rather than the picture.
   * Optional so every artifact part persisted before image artifacts existed
   * still validates on reload.
   */
  image: z
    .object({
      width: z.number().int().positive(),
      height: z.number().int().positive(),
      byteLength: z.number().int().nonnegative(),
      mimeType: z.string().min(1),
    })
    .optional(),
});

export type ArtifactData = z.infer<typeof artifactDataSchema>;

/**
 * `dataSchemas` map for AI SDK `validateUIMessages`. Keys are data-part NAMES
 * (without the `data-` prefix). Register this everywhere chat history is
 * validated, or reloads will reject persisted `data-artifact` parts
 * (validateUIMessages fails any `data-*` part whose name has no schema once a
 * `dataSchemas` map is provided).
 */
export const MERIDIAN_DATA_PART_SCHEMAS = {
  [ARTIFACT_DATA_NAME]: artifactDataSchema,
} as const;

/** A fully-formed `data-artifact` UI message part. */
export interface ArtifactUIPart {
  type: typeof ARTIFACT_PART_TYPE;
  id?: string;
  data: ArtifactData;
}

/**
 * Runtime guard for rendering: is this message part a well-formed artifact
 * part? Malformed data (hand-edited rows, future schema drift) must degrade to
 * "not an artifact" rather than crash the message list.
 */
export function isArtifactUIPart(part: unknown): part is ArtifactUIPart {
  if (typeof part !== 'object' || part === null) return false;
  const candidate = part as { type?: unknown; data?: unknown };
  if (candidate.type !== ARTIFACT_PART_TYPE) return false;
  return artifactDataSchema.safeParse(candidate.data).success;
}

/** Default max artifact content size (chars). Env-overridable. */
export const MERIDIAN_ARTIFACT_MAX_CHARS_DEFAULT = 150_000;

/**
 * Resolve the artifact content size cap from `MERIDIAN_ARTIFACT_MAX_CHARS`
 * (falls back to the default on unset/invalid values).
 */
export function resolveArtifactMaxChars(): number {
  const raw = process.env.MERIDIAN_ARTIFACT_MAX_CHARS;
  if (!raw) return MERIDIAN_ARTIFACT_MAX_CHARS_DEFAULT;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return MERIDIAN_ARTIFACT_MAX_CHARS_DEFAULT;
  return parsed;
}

/**
 * Callback that delivers a finished artifact onto the UI message stream.
 * Wired by the streaming handler (`main.ts`) to
 * `writer.write({ type: 'data-artifact', id, data })`.
 */
export type EmitArtifact = (artifact: ArtifactData) => void;

/**
 * Late-binding holder for the emitter. Tool maps are built BEFORE the stream
 * writer exists (e.g. hub's `getChatToolsForStream` runs in the route handler;
 * the writer only exists inside `createUIMessageStream`'s `execute`), so tools
 * close over this ref and the streaming handler fills `current` once the
 * writer is live.
 */
export interface ArtifactEmitterRef {
  current?: EmitArtifact;
}

/** Create an empty emitter ref (filled by the streaming handler per request). */
export function createArtifactEmitterRef(): ArtifactEmitterRef {
  return {};
}
